import type { Ref } from "react";
import type { TFunction } from "i18next";
import { ActionIcon, Badge, Box, Button, Group, List, Text } from "@mantine/core";
import { IconChevronRight, IconZoomIn } from "@tabler/icons-react";
import { legendItems } from "../legend";
import type { LiveChip } from "../live";
import type { SnapKind } from "../snap";
import { Chip } from "../../ui/Chip";
import { Hint } from "../../ui/Hint";

// The catalogue keys, not the copy: a module constant is built once, at import, and a
// translated string frozen there would still be in the boot language after a switch.
const ZOOM_PRESETS: Array<{ labelKey: string; spanMs: number }> = [
  { labelKey: "timeline.zoom7d", spanMs: 7 * 86_400_000 },
  { labelKey: "timeline.zoom1d", spanMs: 86_400_000 },
  { labelKey: "timeline.zoom1h", spanMs: 3_600_000 },
  { labelKey: "timeline.zoom1min", spanMs: 60_000 },
];

/** Keys rather than copy, for the reason `ZOOM_PRESETS` gives above. */
const SNAP_LABEL_KEYS: Record<SnapKind, string> = {
  icy: "timeline.snapIcy",
  scheduled: "timeline.snapScheduled",
  segment: "timeline.snapSegment",
  grid: "timeline.snapGrid",
};

/**
 * The ink each kind reports itself in: the well's own colour where the snap is derived from the
 * archive, and the schedule's warning colour where it is derived from a promise about the
 * future. Theme colour names the component hands to a `c=` prop, because the hint around it is
 * one line of text and its two words are the only thing that carries the distinction.
 */
const SNAP_COLOURS: Record<SnapKind, string> = {
  icy: "accent",
  scheduled: "warn",
  // Named ink rather than nothing: the hint this sits inside is `dimmed`, so leaving the
  // colour off would inherit that and quietly dim the two kinds that have no ink of their own.
  segment: "var(--mantine-color-text)",
  grid: "var(--mantine-color-text)",
};

interface ZoomControlsProps {
  t: TFunction;
  phone: boolean;
  zoomsOpen: boolean;
  toggleZooms: () => void;
  presetsRef: Ref<HTMLDivElement>;
  presetsWidth: number;
  setSpan: (spanMs: number) => void;
  zoomBy: (factor: number) => void;
}

/**
 * Five buttons the reader needs once they know they want them, and never before. Behind a
 * chevron they cost one glyph of toolbar instead of a row -- which is the difference between
 * the presets fitting on a phone and being cut off it.
 */
function ZoomControls({
  t,
  phone,
  zoomsOpen,
  toggleZooms,
  presetsRef,
  presetsWidth,
  setSpan,
  zoomBy,
}: ZoomControlsProps) {
  return (
    <Group gap={6} wrap="nowrap" role="group" aria-label={t("timeline.zoomGroup")}>
      {/* An `ActionIcon`, not a `Chip`: the pill border is how this toolbar says "a thing
          that changes the canvas", and the disclosure changes nothing but its own row.
          The magnifier says what is behind the chevron -- one glyph of toolbar is cheap,
          but a chevron alone names nothing and is a coin flip on a first visit.
          `transparent` rather than the themed `subtle`, because this row's hover language
          is ink and `subtle` would light a grey square behind it. The width is freed from
          Mantine's square icon box -- two glyphs live in here, not one -- and the 2px
          between them rides on the icon slot Mantine wraps children in, which is now what
          sits between the button and the svgs. `p={0}` goes with that freed width: the
          square box hid the UA's own button padding, and an `auto` width does not.
          The ink is a pair of Mantine's own variables and not a `c` prop, because `c` is
          inline and would beat the `:hover` rule the pair exists for -- dim at rest, lit
          on hover, the same two colours as the chips beside it. */}
      <ActionIcon
        variant="transparent"
        flex="none"
        w="auto"
        miw="auto"
        p={0}
        vars={() => ({
          root: {
            "--ai-color": "var(--mantine-color-dimmed)",
            "--ai-hover-color": "var(--mantine-color-text)",
          },
        })}
        styles={{ icon: { gap: 2 } }}
        aria-label={t("timeline.zoomPresets")}
        aria-expanded={zoomsOpen}
        title={zoomsOpen ? t("timeline.hideZoomPresets") : t("timeline.showZoomPresets")}
        onClick={toggleZooms}
      >
        <IconZoomIn size={15} stroke={1.5} aria-hidden="true" style={{ display: "block" }} />
        {/* One glyph, rotated -- a second path for the other direction is two chances to
            draw a chevron that does not match its twin. Only this one turns, so the
            magnifier beside it stays upright. Same duration as the roll-out it announces,
            so the arrow and the presets arrive together. */}
        <IconChevronRight
          size={15}
          stroke={1.5}
          aria-hidden="true"
          style={{
            display: "block",
            transition: "transform 160ms ease",
            transform: zoomsOpen ? "rotate(180deg)" : undefined,
          }}
        />
      </ActionIcon>
      {/* `inert` and not `display: none`, because the roll-out animates from the presets'
          own width -- they stay laid out, so they also stay tabbable unless something says
          otherwise. This is that something. The clip lives here rather than on the presets,
          so that the presets can be wider than the box that holds them.
          A measured pixel width and not a `0fr` -> `1fr` grid track: this box is a flex
          item, so its width is its own max-content contribution, and Chrome derives that
          from the previous frame's `fr` resolution. The track then reports the value it
          had before the toggle -- open renders closed and closed renders open, forever,
          with no frame in between that is merely late. */}
      <Box
        inert={!zoomsOpen}
        style={{
          width: zoomsOpen ? presetsWidth : 0,
          // The extra half of the gap after the chevron, carried by the thing that rolls
          // out rather than by the row's `gap`: closed, there is nothing to be spaced away
          // from, and a static gap would leave that space sitting between the chevron and
          // the live pill.
          marginLeft: zoomsOpen ? 6 : undefined,
          overflow: "hidden",
          transition: "width 160ms ease, margin-left 160ms ease",
        }}
      >
        {/* Rolling out, not squashing: the chips keep their width while the box has none
            yet. Said once on the container rather than as `flex: none` on every child,
            because a `Chip` takes no style prop -- a `max-content` flex box has no negative
            free space, so nothing inside it can shrink. It is also what makes the measure
            above independent of the box measuring it. */}
        <Group ref={presetsRef} gap={6} wrap="nowrap" w="max-content">
          {/* Two groups side by side, and not the nudge pair nested inside the presets:
              Mantine squares a group's shared corners with a *descendant* selector, so a
              nested group is counted as one more sibling and the last preset stops being
              `:last-child` -- a square right edge on `1 min` facing the gap, which reads as
              a button somebody forgot to round. Each group squares only its own seams. */}
          <Button.Group>
            {ZOOM_PRESETS.map((preset) => (
              <Chip key={preset.labelKey} onClick={() => setSpan(preset.spanMs)}>
                {t(preset.labelKey)}
              </Chip>
            ))}
          </Button.Group>
          {/* Gone on a phone, where the toolbar has no width to spare. A `display: none`
              flex child spends no gap either, so the row closes up on its own. */}
          <Button.Group display={phone ? "none" : undefined}>
            <Chip aria-label={t("timeline.zoomIn")} onClick={() => zoomBy(0.5)}>
              +
            </Chip>
            <Chip aria-label={t("timeline.zoomOut")} onClick={() => zoomBy(2)}>
              −
            </Chip>
          </Button.Group>
        </Group>
      </Box>
    </Group>
  );
}

export interface TimelineToolbarProps extends ZoomControlsProps {
  coarse: boolean;
  editable: boolean;
  snapNote: SnapKind | null;
  live: LiveChip;
  enterLive: (andPlay: boolean) => void;
  chatLane: boolean;
  /** Whether the window carries a listened tracker, which is what puts its key in the legend. */
  listened: boolean;
}

/** The row above the canvas: what it shows, whether it is live, and what the colours mean. */
export function TimelineToolbar({
  coarse,
  editable,
  snapNote,
  live,
  enterLive,
  chatLane,
  listened,
  ...zoom
}: TimelineToolbarProps) {
  const { t, phone } = zoom;
  return (
    <Group gap={phone ? 6 : 10} px={phone ? 10 : 16} py={phone ? 6 : 8} wrap="nowrap">
      <ZoomControls {...zoom} />
      {editable && (
        <Text span fz={11} c="dimmed">
          {snapNote ? (
            // Which kind won matters: "snapped to a title change" and "snapped to the
            // schedule" are very different claims about how much to trust the boundary.
            <Text span inherit c={SNAP_COLOURS[snapNote]}>
              {t(SNAP_LABEL_KEYS[snapNote])}
            </Text>
          ) : (
            t("timeline.dragHint")
          )}
        </Text>
      )}
      {/* Holds the live pill and the legend's trigger at the right edge, away from the
          controls that change what the canvas shows. The corner is where anything
          explanatory belongs. */}
      <Group gap={12} ml="auto" wrap="nowrap">
        {/* The one live affordance on the page, and one pill in both states -- see
            liveChip(). Red it is reporting that the playhead is at the edge; grey it is
            offering to put it there, and only then is it a button at all, which is what
            carries the pointer cursor and the tooltip. The word never changes: a slot that
            swapped LIVE for GO LIVE read as two different controls. */}
        <Badge
          mih={coarse ? 36 : undefined}
          {...(live === "state"
            ? ({
                color: "danger",
                variant: "filled",
                title: t("timeline.pinnedToLive"),
              } as const)
            : ({
                // Mantine's grey `light` is a wash two shades off the page and vanishes on
                // this palette. The raised surface with dimmed ink is what the header's own
                // quiet pill already wears, so the page has one grey and not two.
                bg: "var(--mantine-color-default)",
                c: "dimmed",
                component: "button",
                type: "button",
                title: t("timeline.goLiveTitle"),
                onClick: () => enterLive(true),
                // Badge pins `cursor: default`, which is right for the reporting half and
                // wrong the moment the same element becomes a button.
                style: { cursor: "pointer" },
              } as const))}
        >
          {t("live.live")}
        </Badge>
        {/* The colour key, behind one glyph. It is read once and then never again, and it
            was costing the toolbar a row of swatches at every width wide enough to keep
            them -- and being hidden outright below that, which is where a reader who does
            not know the colours yet actually is. Hover explains it in passing; a press
            pins it open for anyone who wants to read it line by line. */}
        <Hint aria-label={t("timeline.legendHint")}>
          {/* No colours here on purpose. Every swatch is painted inline from
              timeline/theme.ts, which is the same palette the canvas draws with -- see
              legend.ts. Stacked, because it lives in a panel now: a row of a dozen swatches
              only fitted on a wide window, which is exactly the reader who least needed it.
              `listStyleType="none"` is what zeroes the list's own margin and padding; the
              column and its gap have no `List` prop, because the rows are `Text` elements
              rather than `List.Item`s and Mantine's `spacing` only reaches the latter. */}
          <List
            listStyleType="none"
            fz={11}
            c="dimmed"
            style={{ display: "flex", flexDirection: "column", gap: 7 }}
          >
            {legendItems({
              chat: chatLane,
              listened,
              t,
            }).map((item) => (
              // `inherit`, or Mantine's own font size would undo the 11px the list sets on
              // itself: a `Text` carries a size whether it was asked for one or not.
              <Text
                component="li"
                key={item.key}
                title={item.hint}
                inherit
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  style={{
                    flex: "none",
                    width: 12,
                    height: 10,
                    borderWidth: 1,
                    borderRadius: 2,
                    ...item.swatch,
                  }}
                />
                {item.label}
              </Text>
            ))}
          </List>
        </Hint>
      </Group>
    </Group>
  );
}
